/**
 * Thin wrapper around Playroom Kit (v0.0.96).
 *
 * Responsibilities:
 *   - `insertCoin({ turnBased: true, auth: true, persistentMode: true })` for async PvP.
 *   - Terafab compute tick persistence via `getPersistentData` / `setPersistentData`.
 *   - Emergency Backup reward (+500 Compute) on first non-guest social login.
 *
 * Keep the surface small — scenes should not import from `playroomkit` directly.
 */

import {
  insertCoin,
  getPersistentData,
  setPersistentData,
  myPlayer,
  onPlayerJoin,
  isHost,
  getState,
  setState,
  getChallengeId,
} from 'playroomkit';

import { GAME, STATE_KEYS } from '@/config/GameConfig';

type ComputeListener = (next: number) => void;

let ready = false;
let compute = 0;
const listeners = new Set<ComputeListener>();
let tickTimer: ReturnType<typeof setInterval> | null = null;

export async function bootPlayroom(): Promise<void> {
  if (ready) return;

  await insertCoin({
    // Async PvP persistence per product brief.
    turnBased: true,
    // SpaceX-style anonymous Instant Play by default, upgradeable via Emergency Backup.
    auth: true,
    persistentMode: true,
    maxPlayersPerRoom: 2,
    defaultStates: {
      voidIndex: 0.0,
      lanePressure: [0, 0, 0, 0, 0],
    },
  });

  // Resume persistent Terafab ledger.
  const stored = await getPersistentData(STATE_KEYS.COMPUTE);
  const lastTick = await getPersistentData(STATE_KEYS.LAST_TICK);
  compute = typeof stored === 'number' ? stored : 0;

  // Offline accrual: award any ticks that elapsed while the Commander was away.
  if (typeof lastTick === 'number' && lastTick > 0) {
    const elapsed = Date.now() - lastTick;
    const offlineTicks = Math.max(0, Math.floor(elapsed / GAME.COMPUTE_TICK_MS));
    if (offlineTicks > 0) {
      compute += offlineTicks * GAME.COMPUTE_PER_TICK;
      await setPersistentData(STATE_KEYS.COMPUTE, compute);
    }
  }
  await setPersistentData(STATE_KEYS.LAST_TICK, Date.now());
  emit();

  // Live tick — fires every 60s while the tab is open.
  tickTimer = setInterval(tickTerafab, GAME.COMPUTE_TICK_MS);

  onPlayerJoin((p) => {
    // Tag newly joined commanders with their team color for the battle front.
    p.setState('team', isHost() ? 'friendly' : 'hostile', true);
  });

  ready = true;
}

async function tickTerafab(): Promise<void> {
  compute += GAME.COMPUTE_PER_TICK;
  await setPersistentData(STATE_KEYS.COMPUTE, compute);
  await setPersistentData(STATE_KEYS.LAST_TICK, Date.now());
  emit();
}

function emit(): void {
  for (const fn of listeners) fn(compute);
}

export function onComputeChange(fn: ComputeListener): () => void {
  listeners.add(fn);
  fn(compute);
  return () => listeners.delete(fn);
}

export function getCompute(): number {
  return compute;
}

/**
 * Emergency Backup: award once-per-profile Compute for linking a non-guest identity.
 *
 * Playroom's `insertCoin({ auth: true })` already exposes a social login. After the
 * user links, `myPlayer().getProfile()` returns a profile with a stable non-guest id.
 * We gate the reward on persistent storage so it can't be re-claimed.
 */
export async function claimBackupReward(): Promise<{ awarded: boolean; total: number }> {
  const alreadyClaimed = await getPersistentData(STATE_KEYS.BACKUP_CLAIMED);
  if (alreadyClaimed) return { awarded: false, total: compute };

  // Only award when the profile looks real (non-guest). We check defensively:
  // different Playroom builds expose the flag under `isGuest` or a `guest` marker.
  const me = safeMyPlayer();
  const profile = (me?.getProfile?.() ?? {}) as { id?: string; isGuest?: boolean };
  const looksAuthenticated = Boolean(profile?.id) && profile?.isGuest !== true;
  if (!looksAuthenticated) return { awarded: false, total: compute };

  compute += GAME.BACKUP_REWARD;
  await Promise.all([
    setPersistentData(STATE_KEYS.COMPUTE, compute),
    setPersistentData(STATE_KEYS.BACKUP_CLAIMED, true),
  ]);
  emit();
  return { awarded: true, total: compute };
}

/** Shared world state (non-persistent). */
export const world = {
  getVoidIndex: (): number => (getState('voidIndex') as number | undefined) ?? 0,
  setVoidIndex: (v: number): void => setState('voidIndex', v, true),
  getLanePressure: (): number[] =>
    (getState('lanePressure') as number[] | undefined) ?? [0, 0, 0, 0, 0],
  setLanePressure: (p: number[]): void => setState('lanePressure', p, true),
  challengeId: (): string => getChallengeId?.() ?? 'solo',
};

function safeMyPlayer(): ReturnType<typeof myPlayer> | null {
  try {
    return myPlayer();
  } catch {
    return null;
  }
}

export function shutdownPlayroom(): void {
  if (tickTimer) clearInterval(tickTimer);
  tickTimer = null;
}
